import React from 'react';
import { motion } from 'framer-motion';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Sidebar Links configuration
const NAV_LINKS = [
    {
        name: 'Licenses',
        icon: 'fa-certificate',
        desc: 'Renew, print, or download your practice and licenses.',
        color: 'text-[#ebcab3]',
        underline: 'border-[#ebcab3]',
        dot: 'bg-sky-500',
        hideForStaff: true, // Hide from staff / admin users - true
    },
    {
        name: 'Licensing Admin',
        icon: 'fa-users',
        desc: 'Manage Licensing Processes/ Approvals',
        color: 'text-[#ebcab3]',
        underline: 'border-[#ebcab3]',
        dot: 'bg-sky-500',
        staffOnly: true // visible to staff / admin users
    },
    {
        name: 'News',
        icon: 'fa-newspaper',
        desc: 'Manage all the News and Resources',
        color: 'text-[#ebcab3]',
        underline: 'border-[#ebcab3]',
        dot: 'bg-sky-500',
        staffOnly: true
    },
    {
        name: 'Resources',
        icon: 'fa-book-open',
        desc: 'Manage all the complaints submitted to the system',
        color: 'text-[#ebcab3]',
        dot: 'bg-sky-500',
        staffOnly: true
    },
    {
        name: 'Complaints',
        icon: 'fa-hand',
        desc: 'Manage all the complaints submitted to the system',
        color: 'text-[#ebcab3]',
        dot: 'bg-sky-500',
        staffOnly: true
    },
    {
        name: 'Compliments',
        icon: 'fa-hands-clapping',
        desc: 'Manage all the compliments submitted to the system',
        color: 'text-[#ebcab3]',
        dot: 'bg-sky-500',
        staffOnly: true
    },
    {
        name: 'User Management',
        icon: 'fa-user',
        desc: '',
        color: 'text-[#ebcab3]',
        underline: 'border-[#ebcab3]',
        dot: 'bg-sky-500',
        staffOnly: true
    },
    {
        name: 'ID Badge',
        icon: 'fa-id-badge',
        desc: 'Apply for and manage your official LSB professional ID card.',
        color: 'text-[#ebcab3]',
        underline: 'border-[#ebcab3]',
        dot: 'bg-sky-500',
        hideForStaff: true, // Hide from staff (admin) users - true
    },
    {
        name: 'Assistants',
        icon: 'fa-user-group',
        desc: 'View or update your registered surveying assistants.',
        color: 'text-[#ebcab3]',
        underline: 'border-[#ebcab3]',
        dot: 'bg-sky-500',
        staffOnly: false,
        hideForStaff: true, // Hide from staff (admin) users - true
    },
    {
        name: 'Events',
        icon: 'fa-calendar-alt',
        desc: 'Apply for upcoming events or track your registrations.',
        color: 'text-[#ebcab3]',
        underline: 'border-[#ebcab3]',
        dot: 'bg-sky-500',
    },
    {
        name: 'Reset Password',
        icon: 'fa-fingerprint',
        desc: 'Reset your personal account password',
        color: 'text-[#ebcab3]',
        underline: 'border-[#ebcab3]',
        dot: 'bg-sky-500',
        hideForStaff: false
    },
    {
        name: 'Whistleblow',
        icon: 'fa-circle-exclamation',
        desc: 'Submit new complaints or check complaint statuses.',
        color: 'text-[#ebcab3]',
        underline: 'border-[#ebcab3]',
        dot: 'bg-sky-500',
    },
    {
        name: 'Help Center',
        icon: 'fa-circle-question',
        desc: 'Access our help center and frequently asked questions.',
        color: 'text-[#ebcab3]',
        underline: 'border-[#ebcab3]',
        dot: 'bg-sky-500',
        hideForStaff: true // Hide from staff (admin) users - true
    },
];

// Profile Section
function ProfileSection({ userDetails }) {
    let profilePicUrl;
    try {
        profilePicUrl = require('./defaultProfilePic.png');
    } catch {
        profilePicUrl = '';
    }

    if (userDetails && userDetails.user && userDetails.user.profile_pic) {
        if (userDetails.user.profile_pic.startsWith('http')) {
            profilePicUrl = userDetails.user.profile_pic;
        } else {
            profilePicUrl = `https://portal.lsb.go.ke/media/${userDetails.user.profile_pic}`;
        }
    }
    const name = userDetails?.user?.name || 'John Doe';
    const status = userDetails?.user?.status || 'Surveyor';

    return (
        <div className="flex flex-col justify-center items-center mb-3">
            <img
                src={profilePicUrl}
                alt="surveyor"
                className="w-16 h-16 rounded-full object-cover border-2 border-[#ebcab3] transition-all duration-300 bg-gray-800 object-top"
                onError={e => {
                    e.target.onerror = null;
                    try {
                        e.target.src = require('./defaultProfilePic.png');
                    } catch {
                        e.target.src = '';
                    }
                }}
            />

            <span
                className="font-semibold text-gray-100 tracking-wide truncate max-w-[10rem] uppercase mt-2"
                title={name}
            >
                {name}
            </span>

            <small className="text-[#ebcab3] rounded-md font-semibold uppercase underline underline-offset-2">
                {status} Surveyor
            </small>
        </div>
    );
}

// Sidebar nav button
function SidebarNavButton({ tab, activeTab, setActiveTab, setIsSidebarOpen }) {
    const isActive = activeTab === tab.name;

    return (
        <button
            type="button"
            onClick={() => {
                setActiveTab(tab.name);
                if (typeof window !== "undefined" && window.innerWidth < 1024 && typeof setIsSidebarOpen === "function") setIsSidebarOpen(false);
            }}
            className={`
                    flex items-center gap-2 px-3 transition-all duration-200 focus:outline-none text-left
                    ${isActive
                    ? `text-white font-semibold shadow ${tab.underline}`
                    : 'text-gray-300 hover:text-white'
                }
                `}
            aria-current={isActive ? "page" : undefined}
            aria-label={tab.desc}
            tabIndex={0}
            title={tab.desc}
        >
            <i className={`fa ${tab.icon} text-lg w-6 text-left ${tab.color}`} aria-hidden="true" />
            <span className="flex-1 font-medium text-left truncate">{tab.name}</span>
            {isActive && (
                <span className={`ml-auto w-2 h-2 rounded-full ${tab.dot} shadow ring-1 ring-white`} aria-hidden="true"></span>
            )}
        </button>
    );
}

// Utility to get app version
function getAppVersion() {
    let version = '1.0.0';
    if (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.REACT_APP_VERSION) {
        version = import.meta.env.REACT_APP_VERSION;
    } else if (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.VITE_APP_VERSION) {
        version = import.meta.env.VITE_APP_VERSION;
    } else if (typeof process !== "undefined"
        && process.env
        && (process.env.REACT_APP_VERSION || process.env.VITE_APP_VERSION)) {
        version = process.env.REACT_APP_VERSION || process.env.VITE_APP_VERSION;
    }
    return version;
}

/**
 * Returns true if the user is 'licensed', based on userDetails prop.
 * This logic assumes that:
 *   1. If userDetails.user.status === 'Licensed', the user is licensed.
 *   2. You may need to adjust this if your logic or backend differs.
 */
function isUserLicensed(userDetails) {
    if (userDetails && userDetails.user && typeof userDetails.user.status === "string") {
        return userDetails.user.status.toLowerCase().includes('licensed');
    }
    return false;
}

const SidebarNav = React.memo(function SidebarNav({
    userDetails,
    activeTab,
    setActiveTab,
    isSidebarOpen,
    setIsSidebarOpen,
    handleLogout,
}) {
    const navigate = useNavigate();

    // Get account type from localStorage, for sidebar filtering
    let accountType = undefined;
    if (typeof window !== "undefined") {
        accountType = localStorage.getItem('account_type');
    }

    // Staff check effect
    useEffect(() => {
        if (typeof window !== "undefined") {
            const localAccountType = localStorage.getItem('account_type');
            // Only redirect if account_type is NOT staff AND NOT licensed
            if (localAccountType !== 'staff' && localAccountType !== 'licensed') {
                navigate('/unauthorized', { replace: true });
            }
        }
    }, [navigate]);

    // ESC key logic and overlay remain but may not be meaningful without hamburger
    React.useEffect(() => {
        if (!isSidebarOpen) return;
        const handler = (e) => {
            if (e.key === "Escape" && typeof setIsSidebarOpen === "function") {
                setIsSidebarOpen(false);
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [isSidebarOpen, setIsSidebarOpen]);

    const [isClient, setIsClient] = React.useState(false);
    React.useEffect(() => { setIsClient(true); }, []);

    let showOverlay = false;
    if (typeof window !== "undefined" && typeof isSidebarOpen === "boolean" && isSidebarOpen && isClient && window.innerWidth < 1024) {
        showOverlay = true;
    }

    const appVersion = getAppVersion();

    // Sidebar width class: always wide now
    const sidebarWidth = "w-64 px-6";

    // Always visible sidebar
    const sidebarVisible = true;

    // Determine if the user is licensed
    const licensed = isUserLicensed(userDetails);

    // Filter navigation links according to staff status
    const filteredNavLinks = NAV_LINKS.filter(tab => {
        if (accountType === 'staff') {
            if (tab.staffOnly) return true;
            if (tab.hideForStaff) return false;
            // Show tabs for staff that are not marked to hide
            return true;
        }
        // For licensed/non-staff: show tabs that are not staffOnly and not explicitly restricted
        return !tab.staffOnly;
    });

    return (
        <div className="fixed inset-y-0 left-0 z-30 flex flex-col">
            {/* Overlay could also be removed, but left in case sidebar open state is used elsewhere */}
            {showOverlay && (
                <div
                    className="bg-black/60 transition-opacity duration-300 fixed inset-0 z-40"
                    onClick={() => typeof setIsSidebarOpen === "function" && setIsSidebarOpen(false)}
                />
            )}
            <motion.nav
                initial={{ x: -280, opacity: 0 }}
                animate={{ x: sidebarVisible ? 0 : -280, opacity: 1 }}
                exit={{ x: -280, opacity: 0 }}
                transition={{ duration: 0.35, ease: "easeInOut" }}
                className={`bg-gray-900 text-white ${sidebarWidth} py-3 flex flex-col h-screen ${showOverlay ? 'transition-transform' : ''}
                    ${sidebarVisible ? 'translate-x-0' : '-translate-x-full'}
                `}
                aria-label="Sidebar navigation"
                style={{ height: "100vh" }} // Ensures the sidebar stretches to the full viewport height
                tabIndex={-1}
            >
                {/* Profile section */}
                <ProfileSection userDetails={userDetails} />

                <nav className="flex-1 w-full" aria-label="Sidebar main navigation">
                    <div className="space-y-2 flex flex-col items-start">
                        {filteredNavLinks.map(tab => (
                            <SidebarNavButton
                                key={tab.name}
                                tab={tab}
                                activeTab={activeTab}
                                setActiveTab={setActiveTab}
                                setIsSidebarOpen={setIsSidebarOpen}
                            />
                        ))}
                    </div>
                </nav>

                <div className="mt-4 w-full flex flex-col items-center">
                    <button
                        type="button"
                        className="flex items-center justify-center gap-3 w-full max-w-xs relative group transition duration-200 bg-red-600 text-white font-semibold py-2 rounded-full shadow-lg focus:outline-none focus:ring-4 focus:ring-red-500/50 hover:bg-red-700 hover:scale-105 active:scale-95 px-4"
                        onClick={typeof handleLogout === "function" ? handleLogout : undefined}
                        title="Logout"
                        aria-label="Log out of your account"
                        tabIndex={0}
                    >
                        <span className="relative flex items-center justify-center">
                            <i className="fa-solid fa-power-off text-white text-lg group-hover:animate-pulse" aria-hidden="true"></i>
                        </span>
                        <span className="ml-2 tracking-wide font-bold">
                            Log Out
                        </span>
                        <span className="absolute right-4 opacity-0 group-hover:opacity-70 text-xs transition-opacity pointer-events-none">
                            See you soon!
                        </span>
                    </button>
                </div>

                <div className="mt-3 flex justify-center items-end text-xs text-white font-light select-none w-full">
                    v{appVersion}
                </div>
            </motion.nav>
        </div>
    );
});

export default SidebarNav;
